import { RouteVariables } from 'types/route-variables'
import { Language } from 'types/language'
import { URLObject } from 'types/url-object'
import { ROUTES } from 'constants/routes'
import { store } from 'store'
import { ProducerVisual } from 'store/marketing/types'

/**
 * Gets route based upon current path and language
 *
 * @param path
 * @param language
 *
 * @returns route if found
 */
const getRouteKey = (path: string, language: Language): string => {
  // Check if path is a subroute of the producers path
  const regex = new RegExp(ROUTES.PRODUCERS[language] + '/.+$')

  if (regex.exec(path)) {
    return path.includes(ROUTES.REGISTRATION[language]) ? 'registration' : 'producerInfo'
  }

  // Return the route key or return home if it couldn't be found
  return Object.keys(routes(language)).find((key: string) => path === routes(language)[key]) ?? 'home'
}

/**
 * Returns the redirect route used after the simulation
 *
 * @param {Language} language
 * @returns {string}
 */
export const getSimulationRedirectRoute = (language: Language): string => {
  const { producerVisual } = store.getState().marketing

  if (producerVisual === ProducerVisual.THREE_CHOICES) {
    return routes(language).personalizedOffer
  }

  return routes(language).producers
}

/**
 * Translates path to chosen language
 *
 * @param path
 * @param newLanguage
 * @param oldLanguage
 *
 * @returns URLObject
 */
export const translateRoute = (path: string, newLanguage: Language, oldLanguage: Language): URLObject => {
  // Add old language to path
  const pathWithLanguage = `/${oldLanguage}${path}`

  // Removes leading slash
  // Splits url
  const match = pathWithLanguage.replace(/^\//, '').split('/')

  // Removes trailing slash from path
  // Gets route key
  const routeKey = getRouteKey(pathWithLanguage.replace(/\/$/, ''), match[0] as Language)

  let slug = undefined
  // Finds slug based on certain path signatures
  switch (routeKey) {
    case 'producerInfo':
    case 'registration':
      slug = match[2]
      break
    default:
      break
  }

  return {
    route: replaceRouteVariable(routes(newLanguage)[routeKey], RouteVariables.SLUG, slug),
    slug
  }
}

/**
 * Replaces the slug in the route if it's not undefined
 *
 * @param route
 * @param variable
 * @param slug
 */
const replaceRouteVariable = (route: string, variable: RouteVariables, slug: string | undefined): string => {
  return route.replace(variable, slug !== undefined ? slug : variable)
}

/**
 * Replaces multiple slugs in the route if it's not undefined
 *
 * @param route
 * @param variables
 * @param slugs
 */
const replaceNestedRouteVariable = (route: string, variables: RouteVariables[], slugs: string[] | undefined): string => {
  return variables.reduce((prev: string, curr, idx) => prev.replace(curr, slugs?.length ? slugs[idx] : curr), route)
}

export const routes = (lang: string, slug?: string, slugs?: string[]): Record<string, string> => ({
  home: `/${lang}`,
  about: `/${lang}/${ROUTES.ABOUT[lang]}`,
  faq: `/${lang}/${ROUTES.FAQ[lang]}`,
  terms: `/${lang}/${ROUTES.TERMS_AND_CONDITIONS[lang]}`,
  jobs: `/${lang}/${ROUTES.CAREERS[lang]}`,
  priceList: `/${lang}/${ROUTES.PRICELISTS[lang]}`,
  privacy: `/${lang}/${ROUTES.PRIVACY[lang]}`,
  settlement: `/${lang}/${ROUTES.SETTLEMENT[lang]}`,
  noEan: `/${lang}/no-ean`,
  producers: `/${lang}/${ROUTES.PRODUCERS[lang]}`,
  personalizedOffer: `/${lang}/${ROUTES.PERSONALIZED_OFFER[lang]}`,
  producerInfo: replaceRouteVariable(`/${lang}/${ROUTES.PRODUCER_INFO[lang]}`, RouteVariables.SLUG, slug),
  registration: replaceRouteVariable(`/${lang}/${ROUTES.REGISTRATION[lang]}`, RouteVariables.SLUG, slug),
  referral: `/${lang}/${ROUTES.REFERRAL[lang]}`,
  miniGenerators: `/${lang}/${ROUTES.MINI_GENERATORS[lang]}`,
  projects: `/${lang}/${ROUTES.PROJECTS[lang]}`,
  projectCampingBelgica: `/${lang}/${ROUTES.PROJECT_CAMPING_BELGICA[lang]}`,
  projectEvaDeRoo: `/${lang}/${ROUTES.PROJECT_EVA_DE_ROO[lang]}`,
  projectBrusselsBeerProject: `/${lang}/${ROUTES.PROJECT_BRUSSELS_BEER_PROJECT[lang]}`,
  projectIceland: `/${lang}/${ROUTES.PROJECT_ICELAND[lang]}`,
  projectFestivals: `/${lang}/${ROUTES.PROJECT_FESTIVALS[lang]}`,
  pricing: `/${lang}/${ROUTES.PRICING[lang]}`,
  faqPriceIncrease: `/${lang}/${ROUTES.FAQ_PRICE_INCREASE[lang]}`,
  honestEnergy: `/nl/${ROUTES.HONEST_ENERGY['nl']}`,
  generator: `${lang}/${ROUTES.BECOME_GENERATOR[lang]}`,
  encyclopedia: `/${lang}/${ROUTES.ENCYCLOPEDIA[lang]}`,
  encyclopediaCategory: replaceRouteVariable(`/nl/${ROUTES.ENCYCLOPEDIA_CATEGORY['nl']}`, RouteVariables.CATEGORY, slug),
  encyclopediaSubCategory: replaceNestedRouteVariable(
    `/nl/${ROUTES.ENCYCLOPEDIA_SUB_CATEGORY['nl']}`,
    [RouteVariables.CATEGORY, RouteVariables.SUB_CATEGORY],
    slugs
  ),
  encyclopediaArticle: replaceNestedRouteVariable(
    `/nl/${ROUTES.ENCYCLOPEDIA_ARTICLE['nl']}`,
    [RouteVariables.CATEGORY, RouteVariables.SUB_CATEGORY, RouteVariables.ARTICLE],
    slugs
  )
})
